export type PersonClass = "all" | string;
export type ClassKey = "student" | "staff" | "security_guard" | "person";

export function getDistributionTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function getCurrentHourInTimezone(timezone = getDistributionTimezone()): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    hour12: false,
  }).formatToParts(new Date());
  let hour = Number(parts.find((part) => part.type === "hour")?.value ?? 0);
  if (hour === 24) hour = 0;
  return hour;
}

export function getLocalDateString(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Parse hour bucket from API/chart labels such as "02", "02:00", or 14. */
export function parseHourFromLabel(label: string | number): number {
  if (typeof label === "number") return label;
  const part = label.includes(":") ? label.split(":")[0] : label;
  const hour = Number.parseInt(part, 10);
  return Number.isFinite(hour) ? hour : 0;
}

/** Display hour buckets in 12-hour format (e.g. "2 AM", "3 PM"). */
export function formatHourTo12h(label: string | number): string {
  const hour = parseHourFromLabel(label);
  const date = new Date();
  date.setHours(hour, 0, 0, 0);
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    hour12: true,
  });
}

type Hour12Parts = { hour12: number; period: "AM" | "PM" };

function getHour12hParts(hour: number): Hour12Parts {
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const period: Hour12Parts["period"] = hour < 12 ? "AM" : "PM";
  return { hour12, period };
}

/**
 * Heatmap column header: inclusive hour bucket as a range (e.g. "7–8 AM", "12–1 PM").
 * Hour index 7 = 7:00:00 through 7:59:59 in site timezone.
 */
export function formatHeatmapHourRangeLabel(label: string | number): string {
  const startHour = parseHourFromLabel(label);
  const endHour = (startHour + 1) % 24;
  const start = getHour12hParts(startHour);
  const end = getHour12hParts(endHour);
  if (start.period === end.period) {
    return `${start.hour12}–${end.hour12} ${start.period}`;
  }
  return `${start.hour12} ${start.period}–${end.hour12} ${end.period}`;
}

/** Tooltip interval for one heatmap column (e.g. "7:00–7:59 AM"). */
export function formatHeatmapHourIntervalLabel(label: string | number): string {
  const startHour = parseHourFromLabel(label);
  const { hour12, period } = getHour12hParts(startHour);
  return `${hour12}:00–${hour12}:59 ${period}`;
}

/** Site timezone with short abbreviation when available (e.g. "Asia/Calcutta (IST)"). */
export function formatTimezoneLabel(timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(new Date());
    const abbr = parts.find((part) => part.type === "timeZoneName")?.value;
    return abbr ? `${timezone} (${abbr})` : timezone;
  } catch {
    return timezone;
  }
}

export const CLASS_KEYS: ClassKey[] = ["student", "staff", "security_guard", "person"];

export function getActiveClasses(typeOrSelection: PersonClass | string[]): ClassKey[] {
  if (Array.isArray(typeOrSelection)) {
    return getActiveClassesFromSelection(typeOrSelection);
  }
  if (!typeOrSelection || typeOrSelection === "all") return CLASS_KEYS;
  if (typeOrSelection.includes(",")) {
    const active: ClassKey[] = [];
    for (const part of typeOrSelection.split(",").map((s) => s.trim()).filter(Boolean)) {
      if (CLASS_KEYS.includes(part as ClassKey) && !active.includes(part as ClassKey)) {
        active.push(part as ClassKey);
      }
    }
    return active.length ? active : CLASS_KEYS;
  }
  if (CLASS_KEYS.includes(typeOrSelection as ClassKey)) return [typeOrSelection as ClassKey];
  return [];
}

export function getActiveClassesFromSelection(selectedClasses: string[]): ClassKey[] {
  if (!selectedClasses.length || selectedClasses.length >= CLASS_KEYS.length) return CLASS_KEYS;
  const active: ClassKey[] = [];
  for (const value of selectedClasses) {
    if (CLASS_KEYS.includes(value as ClassKey) && !active.includes(value as ClassKey)) {
      active.push(value as ClassKey);
    }
  }
  return active.length ? active : CLASS_KEYS;
}

export function selectedClassesToApiType(
  selectedClasses: string[],
  availableClasses: string[]
): string {
  if (!selectedClasses.length || selectedClasses.length >= availableClasses.length) return "all";
  return selectedClasses.join(",");
}

export const CLASS_COLORS = {
  student: "#3B82F6",
  staff: "#10B981",
  security_guard: "#F59E0B",
  person: "#9CA3AF",
  total: "#6366F1",
  peak: "#EF4444",
} as const;

export type PeopleDistributionFilters = {
  date: string;
  /** Empty array = all classes */
  selectedClasses: string[];
  /** Omit or empty = all cameras */
  cameraId?: string;
};

export type DistributionSummaryCard = {
  title: string;
  value: string;
  subtitle: string;
  timestamp?: string;
  locations?: { name: string; value: number }[];
};

export type OccupancyPoint = {
  hour: string;
  student: number;
  staff: number;
  security_guard: number;
  person: number;
};

export type HourlyPeak = {
  hour: string;
  value: number;
  isPeak?: boolean;
};

export type HourlyClassPeak = {
  hour: string;
  student: number;
  staff: number;
  security_guard: number;
  person: number;
};

export type OccupancyBand = "high" | "mid" | "low";

export type CameraGridItem = {
  cameraId: string;
  cameraName: string;
  locationName: string;
  current: number | null;
  peakToday: number;
  dwell?: number | null;
  studentCount?: number;
  staffCount?: number;
  securityGuardCount?: number;
  personCount?: number;
  /** Site-wide tertile band from backend (not relative to visible cameras). */
  occupancyBand?: OccupancyBand;
};

export type CameraGridSiteTotals = {
  current: number;
  studentCount: number;
  staffCount: number;
  securityGuardCount: number;
  personCount: number;
};

export const CAMERA_GRID_PREVIEW_LIMIT = 8;

export type DistributionKpis = {
  currentOccupancy: number;
  peakOccupancy: number;
  peakTime: string;
  /** Minute boundary in the requested timezone (ISO with offset) for chart peak alignment */
  peakBucketTime?: string;
  /** Minute boundary for peak avg dwell in the requested timezone (ISO with offset) */
  peakDwellBucketTime?: string;
  /** Class breakdown at the peak moment (for chart tooltip). */
  peakOccupancyByType?: Record<string, number>;
  avgOccupancy: number;
  currentAvgDwell: number;
  peakAvgDwell: number;
  peakAvgDwellTime: string;
  avgDwell: number;
  totalVisitors: number;
  currentStudentCount?: number;
  currentStaffCount?: number;
  currentSecurityGuardCount?: number;
  currentPersonCount?: number;
};

export type OccupancyTrendPoint = {
  bucketTime: string;
  totalOccupancy: number;
  occupancyByType: Record<string, number>;
};

export type DwellTrendPoint = {
  bucketTime: string;
  weightedAvgDwell: number;
  totalExits: number;
};

export type HourlyHeatmapRow = {
  label: string;
  metric: "peakOcc" | "avgOcc" | "peakDwell";
  color: "blue" | "purple";
  values: number[];
};

export type PeopleDistributionDashboardResponse = {
  asOf: string;
  kpis: DistributionKpis;
  occupancyTrend: OccupancyTrendPoint[];
  dwellTrend: DwellTrendPoint[];
  hourlyHeatmap?: HourlyHeatmapRow[];
  heatmapHours?: string[];
  throughHour?: number;
  cameraGrid: CameraGridItem[];
  cameraGridTotal: number;
  cameraGridSiteTotals: CameraGridSiteTotals;
  activeClasses: ClassKey[];
  metricDefinitions?: Record<string, string>;
};

export type PeopleDistributionData = {
  summaryCards: DistributionSummaryCard[];
  kpis: DistributionKpis | null;
  occupancySeries: OccupancyPoint[];
  occupancyTrend: OccupancyTrendPoint[];
  dwellTrend: DwellTrendPoint[];
  hourlyPeaks: HourlyPeak[];
  hourlyClassPeaks: HourlyClassPeak[];
  hourlyHeatmap: HourlyHeatmapRow[];
  heatmapHours: string[];
  cameraGrid: CameraGridItem[];
  cameraGridTotal?: number;
  cameraGridSiteTotals?: CameraGridSiteTotals;
  peakHour: string;
  peakTotal: number;
  peakDwellBucketTime?: string;
  activeClasses: ClassKey[];
  /** Snapshot timestamp from atomic dashboard API. */
  asOf?: string;
  /** Last hour to display when viewing today (0–23). */
  throughHour?: number;
};

export const CLASS_LABELS: Record<ClassKey, string> = {
  student: "Students",
  staff: "Staff",
  security_guard: "Security Guard",
  person: "Person",
};

export function formatSecondsShort(seconds: number): string {
  const rounded = Math.round(seconds);
  if (rounded < 60) return `${rounded} s`;
  const mins = Math.floor(rounded / 60);
  const secs = rounded % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

export function formatTimeLabel(
  time: string,
  timezone = getDistributionTimezone()
): string {
  if (!time) return "";

  const trimmed = time.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime()) && trimmed.includes("T")) {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(parsed);
  }

  const match = trimmed.match(/(\d{1,2}):(\d{2})/);
  if (match) {
    const hour = Number.parseInt(match[1], 10);
    const minute = Number.parseInt(match[2], 10);
    if (Number.isFinite(hour) && Number.isFinite(minute)) {
      const date = new Date();
      date.setHours(hour, minute, 0, 0);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
  }

  return trimmed;
}

export function formatAsOfLabel(
  asOf: string,
  timezone = getDistributionTimezone()
): string {
  if (!asOf) return "";
  const parsed = new Date(asOf);
  if (Number.isNaN(parsed.getTime())) return asOf;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(parsed);
}

export function emptyPeopleDistributionData(selectedClasses: string[] = []): PeopleDistributionData {
  return {
    summaryCards: [],
    kpis: null,
    occupancySeries: [],
    occupancyTrend: [],
    dwellTrend: [],
    hourlyPeaks: [],
    hourlyClassPeaks: [],
    hourlyHeatmap: [],
    heatmapHours: [],
    cameraGrid: [],
    peakHour: "",
    peakTotal: 0,
    throughHour: 23,
    activeClasses: getActiveClassesFromSelection(selectedClasses),
  };
}

export function hasDistributionData(data: PeopleDistributionData): boolean {
  return (
    data.kpis != null ||
    data.summaryCards.length > 0 ||
    data.occupancySeries.length > 0 ||
    data.occupancyTrend.length > 0 ||
    data.hourlyPeaks.length > 0
  );
}
